import { MapSourceType, WaypointType } from '@caresend/types';
import { formatUserInitials, nullishFilter, getWaypointLetter, flattenArray } from '@caresend/utils';
import { WHITE_DOT } from '../../assets/icons/cloudIcons.js';
import { MarkerIconName } from '../../components/specific/portal/components/model.js';
import { usePortalStore } from '../../components/specific/portal/store/portal/index.js';
import '@sentry/vue';
import '@fortawesome/fontawesome-svg-core';
import '@fortawesome/sharp-regular-svg-icons';
import 'buefy';
import { mutateColorBrightness, getSeededColor } from '../../functions/color.js';
import '../../components/ui/Icon.vue.js';
import '../../functions/configuration/icons.js';
import './indices.js';
import 'mobile-device-detect';
import '../../functions/tracking/index.js';

const getLocationObjPoint = (item) => {
    const { lng, lat } = item.location || { lng: 0, lat: 0 };
    return [lng, lat];
};
const generateGeoJsonPoint = (coordinates, properties) => ({
    type: 'Feature',
    geometry: { type: 'Point', coordinates },
    properties,
});
const generateMapSources = (id, points) => points.map((point) => {
    var _a, _b;
    return ({
        id: (_b = (_a = point.properties) === null || _a === void 0 ? void 0 : _a.id) !== null && _b !== void 0 ? _b : id,
        iconSize: 0.2,
        iconUrl: WHITE_DOT,
        data: [point],
    });
});
const generateUserPhotoMapSources = (users, shift) => users.map((user) => {
    if (!user.location)
        return null;
    const portalStore = usePortalStore();
    const distance = shift ? portalStore.users.getters.shiftDistance(user) : undefined;
    if (!distance)
        return null;
    return {
        id: user.id,
        iconSize: 0.2,
        iconUrl: WHITE_DOT,
        data: [generateGeoJsonPoint(getLocationObjPoint({
                location: portalStore.users.getters.locationForShift(user),
            }), {
                id: user.id,
                marker: {
                    waypointType: MapSourceType.USER_PHOTO,
                    markerSrc: user.info.picture,
                    initials: formatUserInitials(user),
                    color: mutateColorBrightness(getSeededColor(user.id), -30),
                },
            })],
    };
}).filter(nullishFilter);
/** Map sources */
const generateWaypointMapSources = (draftWaypoints = [], inputWaypoints = []) => {
    var _a, _b, _c, _d, _e;
    const portalStore = usePortalStore();
    const waypoints = portalStore.itinerary.getters.currentWaypoints();
    const itineraryWaypointInfos = waypoints === null || waypoints === void 0 ? void 0 : waypoints.map((waypoint, index) => ({
        waypoint,
        waypointNumber: index + 1,
        waypointStatus: waypoint.status.status,
    })).filter((waypointInfo) => waypointInfo.waypoint.type === WaypointType.PATIENT);
    const waypointPoints = [
        ...draftWaypoints,
        ...inputWaypoints,
    ].map((draftWaypoint) => {
        const isInItinerary = portalStore.itinerary.getters.draftWaypointIDs()[draftWaypoint.id];
        if (isInItinerary) {
            return [];
        }
        const waypointRelatedMapPoints = [];
        const properties = {
            id: draftWaypoint.id,
            marker: {
                waypointType: MapSourceType.DRAFT_WAYPOINTS,
            },
        };
        waypointRelatedMapPoints.push(generateGeoJsonPoint(getLocationObjPoint(draftWaypoint), properties));
        return generateMapSources(draftWaypoint.id, waypointRelatedMapPoints);
    }).flat();
    const itineraryRelatedMapPoints = [];
    const firstWaypointInfo = itineraryWaypointInfos === null || itineraryWaypointInfos === void 0 ? void 0 : itineraryWaypointInfos[0];
    if (firstWaypointInfo) {
        const itinerary = portalStore.itinerary.value;
        const shift = portalStore.itinerary.getters.shift();
        if (shift) {
            const { leaveFrom, leaveTo } = shift;
            /**
           * This data should be the same for all waypoints in the itinerary because
           * it is fetched from leaveFrom on the shift, so it only needs to fetched
           * once on the first waypoint
           */
            if (leaveFrom) {
                itineraryRelatedMapPoints.push(generateGeoJsonPoint(getLocationObjPoint({ location: leaveFrom }), {
                    id: shift.id,
                    marker: {
                        waypointType: MapSourceType.ITINERARY_INDICATED_START_POINT,
                        iconName: MarkerIconName.HOME,
                        waypointNumber: 10,
                    },
                }));
            }
            if (leaveTo) {
                itineraryRelatedMapPoints.push(generateGeoJsonPoint(getLocationObjPoint({ location: leaveTo }), {
                    id: `${itinerary === null || itinerary === void 0 ? void 0 : itinerary.id}endPoint`,
                    marker: {
                        waypointType: MapSourceType.ITINERARY_END_POINT,
                        iconName: MarkerIconName.ARRIVAL,
                        waypointNumber: 10,
                    },
                }));
            }
            const actualDepartureLocation = (_a = firstWaypointInfo.waypoint.transitInfo) === null || _a === void 0 ? void 0 : _a.departureLocation;
            /**
           * We only want to display the first actual starting point on map
           */
            if (actualDepartureLocation) {
                itineraryRelatedMapPoints.push(generateGeoJsonPoint(getLocationObjPoint({ location: actualDepartureLocation }), {
                    id: `${itinerary === null || itinerary === void 0 ? void 0 : itinerary.id}startPoint`,
                    marker: {
                        waypointType: MapSourceType.ITINERARY_ACTUAL_START_POINT,
                        iconName: MarkerIconName.PLAY,
                        waypointNumber: 10,
                    },
                }));
            }
            const nurseLatestLocation = (_c = (_b = portalStore.itinerary.getters.assignedNurse()) === null || _b === void 0 ? void 0 : _b.location) === null || _c === void 0 ? void 0 : _c.current;
            if (nurseLatestLocation) {
                itineraryRelatedMapPoints.push(generateGeoJsonPoint(getLocationObjPoint({ location: nurseLatestLocation }), {
                    id: `${itinerary === null || itinerary === void 0 ? void 0 : itinerary.id}-clinicianLastLocation`,
                    marker: {
                        waypointType: MapSourceType.CLINICIAN_LAST_LOCATION,
                        iconName: MarkerIconName.CLINICIAN,
                        waypointNumber: 10,
                    },
                }));
            }
        }
    }
    const itineraryRelatedMapSources = generateMapSources((_d = firstWaypointInfo === null || firstWaypointInfo === void 0 ? void 0 : firstWaypointInfo.waypoint.id) !== null && _d !== void 0 ? _d : '', itineraryRelatedMapPoints);
    const waypointPointsInItinerary = (_e = itineraryWaypointInfos === null || itineraryWaypointInfos === void 0 ? void 0 : itineraryWaypointInfos.map((waypointInfo) => {
        const waypointRelatedMapPoints = [];
        waypointRelatedMapPoints.push(generateGeoJsonPoint(getLocationObjPoint(waypointInfo.waypoint), {
            id: waypointInfo.waypoint.id,
            marker: {
                waypointLetter: getWaypointLetter(waypointInfo.waypointNumber - 1),
                waypointType: MapSourceType.DRAFT_WAYPOINTS,
                waypointStatus: waypointInfo.waypointStatus,
            },
        }));
        return generateMapSources(waypointInfo.waypoint.id, waypointRelatedMapPoints);
    }).flat()) !== null && _e !== void 0 ? _e : [];
    return [
        ...waypointPoints,
        ...waypointPointsInItinerary,
        ...itineraryRelatedMapSources,
    ];
};
const generatePlaceMapSources = (places) => {
    const portalStore = usePortalStore();
    const waypoints = portalStore.itinerary.getters.currentWaypoints();
    const placeWaypointInfo = waypoints === null || waypoints === void 0 ? void 0 : waypoints.map((waypoint, index) => ({
        waypoint,
        waypointNumber: index + 1,
        waypointStatus: waypoint.status.status,
    })).filter((waypointInfo) => waypointInfo.waypoint.type === WaypointType.PICK_DROP);
    const filteredPlaces = places.filter((place) => !(placeWaypointInfo === null || placeWaypointInfo === void 0 ? void 0 : placeWaypointInfo.find((placeInfo) => placeInfo.waypoint.placeID === place.id)));
    const placePoints = flattenArray(filteredPlaces.map((place) => {
        const properties = {
            id: place.id,
            marker: {
                waypointType: MapSourceType.PLACES,
            },
        };
        return generateMapSources(place.id, [generateGeoJsonPoint(getLocationObjPoint(place), properties)]);
    }));
    const placePointsInItinerary = flattenArray(placeWaypointInfo === null || placeWaypointInfo === void 0 ? void 0 : placeWaypointInfo.map((placeInfo) => {
        const properties = {
            id: placeInfo.waypoint.id,
            marker: {
                waypointType: MapSourceType.PLACES,
                waypointLetter: getWaypointLetter(placeInfo.waypointNumber - 1),
                waypointStatus: placeInfo.waypointStatus,
            },
        };
        return generateMapSources(placeInfo.waypoint.id, [generateGeoJsonPoint(getLocationObjPoint(placeInfo.waypoint), properties)]);
    }));
    return [
        ...placePoints,
        ...placePointsInItinerary,
    ];
};

export { generateGeoJsonPoint, generateMapSources, generatePlaceMapSources, generateUserPhotoMapSources, generateWaypointMapSources, getLocationObjPoint };
